import cx from "classnames"
import { forwardRef } from "react"
import Button, { type ButtonProps, type LinkProps } from "../ButtonComponent"
import {
  type ButtonTemplatePropsType,
  getButtonCommonClassNames,
  getButtonContent,
} from "../helpers"

type InversedCommonPropsType = ButtonTemplatePropsType<Record<string, unknown>>

export type InversedButtonPropsType = InversedCommonPropsType & ButtonProps
export type InversedLinkPropsType = InversedCommonPropsType & LinkProps

const getComputedClassName = ({ className }: InversedCommonPropsType) =>
  cx(className, "cob-Button__inversed")

const _InversedButton = forwardRef<HTMLButtonElement, InversedButtonPropsType>(
  (props: InversedButtonPropsType, ref) => {
    const {
      children,
      icon,
      iconPosition,
      className,
      loading,
      disabled,
      rounded,
      size,
      fullWidth,
      ...restButtonProps
    } = props
    const content = getButtonContent({ children, icon, iconPosition })
    const isDisabled = disabled || loading
    const buttonClassNames = getButtonCommonClassNames({
      disabled: isDisabled,
      rounded,
      size,
      fullWidth,
      className,
    })

    return (
      <Button
        {...restButtonProps}
        className={getComputedClassName({ className: buttonClassNames })}
        ref={ref}
        loading={loading}
        disabled={isDisabled}
      >
        {content}
      </Button>
    )
  },
)
_InversedButton.displayName = "InversedButton"

const InversedLink = forwardRef<HTMLAnchorElement, InversedLinkPropsType>(
  (props: InversedLinkPropsType, ref) => {
    const {
      children,
      icon,
      iconPosition,
      className,
      loading,
      disabled,
      rounded,
      size,
      fullWidth,
      ...restLinkProps
    } = props
    const content = getButtonContent({ children, icon, iconPosition })
    const isDisabled = disabled || loading
    const buttonClassNames = getButtonCommonClassNames({
      disabled: isDisabled,
      rounded,
      size,
      fullWidth,
      className,
    })

    return (
      <Button.Link
        {...restLinkProps}
        className={getComputedClassName({ className: buttonClassNames })}
        ref={ref}
        loading={loading}
        disabled={isDisabled}
      >
        {content}
      </Button.Link>
    )
  },
)
InversedLink.displayName = "InversedLink"

const InversedButton = Object.assign(_InversedButton, { Link: InversedLink })

export default InversedButton
